import { Box } from '@mui/material';
import {
  Category,
  HoldToken,
  Pricing,
  SeatingChart,
  SeatsioSeatingChart,
  SelectableObject,
  TicketTypeJson,
} from '@seatsio/seatsio-react';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { notEmpty } from 'utils/typescript.utils';

import { useLocale } from 'hooks/useLocale';
import { useTranslatedText } from 'hooks/useTranslatedText';
import {
  DEFAULT_CURRENCY,
  MAX_CART_ITEMS,
  type SeatingObjectType,
} from 'restful/tickets';
import { useTrackQuantityChange } from 'services/datalayer/ecomm/useTrackQuantityChange';
import { useCartStore, useCartStoreActions } from 'services/stores/cart';

import BorderStack from 'components/@common/BorderStack';
import LoadingScreen from 'components/@common/LoadingScreen';
import { formatEventDate } from 'components/@events/utils';
import CategorySelector from 'components/@seating/CategorySelector';
import SelectedSeats from 'components/@seating/SelectedSeats';
import {
  SeatingInfo,
  SeatingToken,
  SelectedSeat,
} from 'components/@seating/types';
import { useTicketInfoLookup } from 'components/@tickets/hooks';

interface Props {
  seatingInfo: SeatingInfo;
  seatingToken: SeatingToken | undefined;
  timezone: string;
  onSubmit: () => void;
}

const Content = ({ seatingInfo, seatingToken, timezone, onSubmit }: Props) => {
  const intl = useIntl();
  const { trackAdd, trackRemove } = useTrackQuantityChange();
  const { locale } = useLocale();
  const localize = useTranslatedText();
  const ticketInfoLookup = useTicketInfoLookup();
  const [selectedSeats, setSelectedSeats] = useState<Array<SelectedSeat>>([]);
  const seatingChartRef = useRef<SeatingChart | null>(null);
  const [categories, setCategories] = useState<Array<Category>>([]);
  const [isLoading, setIsLoading] = useState(true);

  const cartItems = useCartStore((state) => state.items);

  const regularTicketsCount = cartItems
    .filter((item) => item.bookObjectsInfo?.length === 0)
    .reduce((total, item) => total + item.quantity, 0);

  const {
    setSeatingToken,
    setSeatingTokenExpired,
    removeCartSeatedItems,
    setSeatedItemToCart,
    removeSeatedItemFromCart,
  } = useCartStoreActions();

  const datetime = formatEventDate({
    locale,
    startDate: seatingInfo.dateTimeRange.start,
    endDate: seatingInfo.dateTimeRange.end,
    timeZone: timezone,
  });

  const availableCategoryKeys = seatingInfo.categories
    .map((category) => category.categoryKey)
    .filter(notEmpty);

  const pricing: Pricing = seatingInfo.categories.map((category) => {
    const { originalPrice, discountedPrice } = ticketInfoLookup(
      category.ticketId,
    );
    return {
      category: category.categoryKey,
      label: localize(category.name),
      price: discountedPrice?.amount ?? 0,
      originalPrice: originalPrice?.amount,
    };
  });

  // When seats.io starts it will call onObjectSelected for all previous selected seats
  // So we need to reset our local state
  const handleSessionInitialized = (session: HoldToken) => {
    setSelectedSeats([]);
    setSeatingToken(session);
  };

  const handleTokenExpired = () => {
    setSeatingTokenExpired();
    removeCartSeatedItems();
  };

  const handleChartRendered = async (chart: SeatingChart) => {
    setIsLoading(false);
    const categories = await chart.listCategories();
    setCategories(categories.filter((category) => !!category.pricing));
    seatingChartRef.current = chart;
    chart.zoomToFilteredCategories();
  };

  const handleSelectSeat = (
    selectedSeat: SelectableObject,
    _selectedTicketType: TicketTypeJson,
  ) => {
    // Ensures we don't copy a complete reference to the chart
    const { chart, ...seat } = selectedSeat;

    const matchingTicket = seatingInfo.categories.find(
      (category) => category.categoryKey === seat.category?.key?.toString(),
    );

    if (matchingTicket) {
      const selectedSeat = {
        ...seat,
        ...matchingTicket,
      };

      // We just deal with seats to make things a lot easier here,
      // When the user confirms we map the seats to something we can work with
      // as internal representation
      setSelectedSeats((prev) => {
        const existing = prev.find(
          (existingSeat) => existingSeat.uuid === seat.uuid,
        );

        if (existing) {
          return [
            ...prev.filter((existingSeat) => existingSeat.uuid !== seat.uuid),
            { ...existing, ...selectedSeat },
          ];
        }

        return [...prev, selectedSeat];
      });

      trackAdd({ id: matchingTicket.ticketId, name: matchingTicket.name });

      setSeatedItemToCart({
        catalogueItemId: matchingTicket.ticketId,
        bookObjectsInfo: [
          {
            label: seat.label, // unique identifier
            own: seat.labels.own,
            section: seat.labels.section,
            parent: seat.labels.parent,
            objectType: (seat.displayObjectType ??
              seat.objectType) as SeatingObjectType,
          },
        ],
        name: matchingTicket.name,
      });
    }
  };

  const handleDeselect = ({ uuid }: { uuid: string }) => {
    setSelectedSeats((prev) => {
      const seatToRemove = prev.find((seat) => seat.uuid === uuid);
      if (seatToRemove) {
        trackRemove({ id: seatToRemove.ticketId, name: seatToRemove.name });
        removeSeatedItemFromCart({
          catalogueItemId: seatToRemove.ticketId,
          bookObject: seatToRemove.label,
        });
      }
      return prev.filter((existingSeat) => existingSeat.uuid !== uuid);
    });
  };

  const holdToken = !!seatingToken ? seatingToken.token : undefined;
  const session = !!seatingToken?.token ? 'manual' : 'start';

  return (
    <Box
      sx={{
        display: 'grid',
        flex: 1,
        gridTemplateColumns: { xs: '1fr', md: '6fr 1fr 5fr' },
        gridTemplateRows: { xs: 'auto 1fr auto', md: 'auto 1fr' },
        mt: { xs: 2, md: 7 },
        mx: { xs: -2, sm: 0 },

        gridTemplateAreas: {
          xs: `'filter' 'seating' 'selection'`,
          md: `"filter . selection" "seating . selection"`,
        },

        alignItems: 'start',
      }}
    >
      <CategorySelector
        subtitle={datetime}
        categories={categories}
        initialFilteredCategory={seatingInfo.focusCategoryKey}
        chartRef={seatingChartRef}
      />
      <Box
        sx={(theme) => ({
          gridArea: 'seating',
          mt: { md: 3 },
          display: 'flex',
          position: 'relative',
          borderBottom: {
            xs: `1px solid ${theme.vars.palette.divider}`,
            md: 'none',
          },
          borderTop: {
            xs: `1px solid ${theme.vars.palette.divider}`,
            md: 'none',
          },
          height: '100%',
        })}
      >
        <BorderStack
          sx={(theme) => ({
            p: 0,
            height: { xs: 'calc(100% - 16px)', md: '70vh' },
            position: 'relative',
            flexGrow: 1,
            overflow: { md: 'hidden' },
            border: {
              xs: 'none',
              md: `1px solid ${theme.vars.palette.divider}`,
            },
          })}
        >
          {/* We need the position absolute to ensure its centered */}
          {isLoading && (
            <LoadingScreen
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            />
          )}

          <Box
            sx={{
              visibility: isLoading ? 'hidden' : 'visible',
              width: '100%',
              height: '100%',
            }}
          >
            <SeatsioSeatingChart
              // Ensures that on mobile we have grow to the full available size
              fitTo="widthAndHeight"
              // session: https://support.seats.io/en/articles/2107909-holding-seats-or-general-admission-places
              session={session}
              maxSelectedObjects={MAX_CART_ITEMS - regularTicketsCount}
              messages={{
                maxSelectionReachedWithNumber: intl.formatMessage(
                  { id: 'seats.messages.max.reached.label' },
                  { amount: MAX_CART_ITEMS },
                ),
              }}
              language={locale}
              workspaceKey={process.env.NEXT_PUBLIC_SEATSIO_WORKSPACE_KEY ?? ''}
              filteredCategories={seatingInfo.focusCategoryKey}
              availableCategories={availableCategoryKeys}
              event={seatingInfo.chartKey}
              region="eu"
              pricing={pricing}
              objectWithoutPricingSelectable={false}
              colorScheme="dark"
              showFullScreenButton={false}
              priceFormatter={(price) =>
                intl.formatNumber(price, {
                  style: 'currency',
                  minimumFractionDigits: 2,
                  currency: DEFAULT_CURRENCY,
                })
              }
              onSessionInitialized={handleSessionInitialized}
              onChartRendered={handleChartRendered}
              onObjectSelected={handleSelectSeat}
              onObjectDeselected={handleDeselect}
              onHoldTokenExpired={handleTokenExpired}
              holdToken={holdToken}
            />
          </Box>
        </BorderStack>
      </Box>
      <SelectedSeats
        subtitle={datetime}
        selectedSeats={selectedSeats}
        onRemoveSeat={handleDeselect}
        onSubmit={onSubmit}
      />
    </Box>
  );
};

export default Content;
